import { lineRange } from './line-range.js';
// Builds the summary stat cards and The Crux findings from a diff result —
// driven entirely by engine output (spec §9), across text, prose and structural
// results.
import { esc } from './escape.js';
import { changeStops } from '../engine/change-stops.js';

// The findings list now has ONE consumer: the saved HTML report. The app shows
// no findings row — the diff below carries the changes, the cards carry the
// counts and the stepper carries the way through them, so a row of truncated
// one-line excerpts above all three was a worse second copy. A report has none
// of that, so it still lists findings, at the full cap.
const CRUX_CAP = 14;

const trunc = (s, n = 64) => {
  const t = String(s).trim();
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
};
const plural = (n, one, many = `${one}s`) => (n === 1 ? one : many);
const val = (v) => esc(trunc(JSON.stringify(v), 40));

/* ---------- how the two sides were matched ---------- */
// The Crux's subtitle. A reader's first question about any comparison is what
// was matched against what — "Records matched by id" is the difference between
// trusting a row-level finding and not. Every format answers it in its own
// terms, and the duplicate-key fallback says so rather than implying a key.
export function cruxSubtitle(result) {
  const kind = result?.render?.kind;
  if (kind === 'records') {
    if (result.warning === 'duplicate-key') return 'Rows matched by position — duplicate keys';
    if (result.warning === 'invalid-key') return 'Rows matched by position — key not usable';
    const key = result.render?.keyColumn || result.keyColumn;
    return key ? `Records matched by ${key}` : 'Rows matched by position';
  }
  if (kind === 'structural') return 'Fields matched by path, not by line';
  if (kind === 'prose') return 'Words matched across the whole document';
  return result?.moved ? 'Lines matched in order, moved blocks kept whole' : 'Lines matched in order';
}

/* ---------- stat cards ---------- */
// `navigable` lists the category ids that have somewhere to jump to; those
// cards render as buttons and the rest stay plain (G81 — never offer a control
// with no destination). `places` adds the leading card — the total
// number of places to review — and `filterable` decides whether a card says it
// filters or that it steps. The HTML export passes none of it, so a saved
// report keeps inert cards with no promise of behaviour it cannot honour.
export function summaryHtml(result, { navigable = null, places = null, filterable = false, model = null } = {}) {
  // The card context is passed, never parked in module state: a builder that
  // threw would otherwise leave the next call rendering the last one's cards.
  const ctx = navigable
    ? { navigable, model: model || changeStops(result), action: filterable ? 'Filter' : 'Step through' }
    : null;
  const card = (n, label, cls, category) => stat(ctx, n, label, cls, category);
  const kind = result.render?.kind;
  const lead = navigable && places != null
    ? leadCard(places)
    : '';
  const out = kind === 'structural' ? structuralCards(result, card)
    : kind === 'records' ? recordCards(result, card)
      : kind === 'prose' ? proseCards(result, card)
        : textCards(result, card);
  return lead + out;
}

export const CATEGORY_LABELS = { all: 'All changes', changed: 'Modified', added: 'Added', removed: 'Removed', moved: 'Moved', typeChanges: 'Type / schema' };

// The leading card counts PLACES, not a sum of the other cards — those are in
// their own units and for prose they overlap (a replacement is counted into
// added and removed and modified at once), so adding them up would be a number
// that means nothing. "4 affected records" is how many rows you will visit.
function leadCard(places) {
  return `<button type="button" class="stat stat-lead" data-category="all" aria-pressed="true" aria-label="All changes — ${places} review ${plural(places, 'location')}"><div class="stat-category" aria-hidden="true">All changes</div><div class="n" data-n="${places}" aria-hidden="true">0</div><div class="l" aria-hidden="true">review ${plural(places, 'location')}</div><div class="act" aria-hidden="true">Review everything</div></button>`;
}

function recordCards(result, card) {
  const cards = [];
  if (result.typeChanges) cards.push(card(result.typeChanges, plural(result.typeChanges, 'schema change', 'schema changes'), 'changed', 'typeChanges'));
  // "modified" for the record class, never "changed": the analysis panel reserves
  // "changed"/"affected" for added + removed + modified together, and two words for
  // two different totals a few centimetres apart is how a reader mistrusts both.
  // Modified leads because it is the common case; added and removed follow.
  if (result.modified) cards.push(card(result.modified, plural(result.modified, 'record modified', 'records modified'), 'changed', 'changed'));
  cards.push(card(result.added, plural(result.added, 'record added', 'records added'), 'added', 'added'));
  cards.push(card(result.removed, plural(result.removed, 'record removed', 'records removed'), 'removed', 'removed'));
  if (result.moved) cards.push(card(result.moved, 'order changed', 'reordered', 'moved'));
  return cards.join('');
}

function textCards(result, card) {
  const cards = [];
  if (result.moved) cards.push(card(result.moved, plural(result.moved, 'block moved', 'blocks moved'), 'moved', 'moved'));
  if (result.modified) cards.push(card(result.modified, plural(result.modified, 'line changed', 'lines changed'), 'changed', 'changed'));
  cards.push(card(result.added, plural(result.added, 'line added', 'lines added'), 'added', 'added'));
  cards.push(card(result.removed, plural(result.removed, 'line removed', 'lines removed'), 'removed', 'removed'));
  return cards.join('');
}

function structuralCards(result, card) {
  const cards = [];
  if (result.typeChanges) cards.push(card(result.typeChanges, plural(result.typeChanges, 'type change ⚠', 'type changes ⚠'), 'moved', 'typeChanges'));
  if (result.modified) cards.push(card(result.modified, plural(result.modified, 'value changed', 'values changed'), 'changed', 'changed'));
  cards.push(card(result.added, 'added', 'added', 'added'));
  cards.push(card(result.removed, 'removed', 'removed', 'removed'));
  return cards.join('');
}

function proseCards(result, card) {
  return [
    card(result.modified, plural(result.modified, 'replacement', 'replacements'), 'changed', 'changed'),
    card(result.added, plural(result.added, 'word added', 'words added'), 'added', 'added'),
    card(result.removed, plural(result.removed, 'word removed', 'words removed'), 'removed', 'removed'),
  ].join('');
}

// `category` is the navigator's join with change-stops.js — an EXPLICIT id, not
// the CSS class: `typeChanges` and `modified` both render with class `changed`,
// so the class cannot identify a category. `cls` stays presentational.
function stat(ctx, n, label, cls, category = cls) {
  // The visual number counts up (aria-hidden so AT doesn't hear each tick); the
  // element carries the final value once via aria-label.
  const plain = esc(String(label).replace(/<[^>]*>/g, ''));
  // Inert cards are the saved report's, where no script will count them up, so
  // they carry the final number; the app's cards start at 0 for animateCounts.
  const inner = `<div class="n" data-n="${n}" aria-hidden="true">${n}</div><div class="l" aria-hidden="true">${label}</div>`;
  if (ctx) {
    const title = CATEGORY_LABELS[category] || label;
    const cat = ctx.model?.categories.find((c) => c.id === category);
    const count = cat?.stopIndexes.length || 0;
    const unit = cat?.unit || '';
    const measurement = unit ? plural(n, unit) : label;
    const action = count ? `${count} ${plural(count, 'location')} · ${ctx.action === 'Filter' ? 'Filter' : 'Review'}` : (n ? 'Summary only · no review location' : 'No changes');
    const content = `<div class="stat-category" aria-hidden="true">${esc(title)}</div><div class="n" data-n="${n}" aria-hidden="true">0</div><div class="l" aria-hidden="true">${esc(measurement)}</div><div class="act" aria-hidden="true">${action}</div>`;
    const aria = `${esc(title)} — ${n} ${esc(measurement)} — ${action}`;
    if (ctx.navigable.includes(category)) return `<button type="button" class="stat ${cls}" data-category="${category}" aria-pressed="false" aria-label="${aria}">${content}</button>`;
    return `<div class="stat ${cls}" data-category="${category}" role="group" aria-label="${aria}">${content}</div>`;
  }
  return `<div class="stat ${cls}" data-category="${category}" role="group" aria-label="${n} ${plain}">${inner}</div>`;
}

/* ---------- The Crux ---------- */
// One finding per PLACE, not per raw change: a prose date edit arrives from the
// engine as two runs (`05→06`, `12→09`) and would read as two context-free
// chips, so the navigation model's grouping decides the list and each entry
// carries the words around the edit. `data-stop` stays on each entry — it costs
// nothing and keeps a report's row addressable by the same id the app uses.
export function cruxHtml(result, { cap = CRUX_CAP, model = null } = {}) {
  const changes = Array.isArray(result?.changes) ? result.changes : null;
  if (!changes) {
    return '<span class="finding"><span class="fk ok">note</span><span>semantic findings are unavailable for this format</span></span>';
  }
  const formatting = result.finalNewline
    ? finding('ch', 'formatting changed', `Final newline ${result.finalNewline === 'added' ? 'added' : 'removed'}.${!changes.length ? ' Text content is unchanged.' : ''}`)
    : '';
  if (!changes.length && formatting) return formatting;
  if (!changes.length) {
    return '<span class="finding"><span class="fk ok">no change</span><span>the two inputs are equivalent</span></span>';
  }
  const nav = model || changeStops(result);
  const entries = cruxEntries(result, nav);
  const shown = formatting + previewEntries(entries, cap).map(entryHtml).join('');
  const extra = entries.length - cap;
  if (extra <= 0) return shown;
  // Name what the overflow counts in. Where one stop is one change (records,
  // structural) that word is still "changes"; where places were merged it is
  // the model's own noun, so this number and the stepper's agree.
  const noun = entries.length === changes.length ? 'change' : nav.stopNoun;
  return `${shown}<span class="finding"><span class="fk ok">+${extra}</span><span>more ${noun}s — see the diff below</span></span>`;
}

// Stops and notes in one list, in the engine's own order: a key warning or an
// order note still belongs in the Crux, it just cannot be jumped to.
function cruxEntries(result, nav) {
  const changes = result.changes || [];
  const entries = [
    ...nav.stops.map((stop) => ({ at: stop.changeIndexes[0], stop, change: changes[stop.changeIndexes[0]] })),
    ...nav.notes.map((i) => ({ at: i, stop: null, change: changes[i] })),
  ];
  entries.sort((a, b) => a.at - b.at);
  return entries.map((e) => ({ ...e, kind: e.change?.kind || 'change' }));
}

function entryHtml(entry) {
  const inner = entry.stop && entry.stop.context
    ? passageFinding(entry.stop)
    : findingBody(entry.change);
  if (!entry.stop) return `<span class="finding">${inner}</span>`;
  return `<span class="finding" data-stop="${esc(entry.stop.id)}">${inner}</span>`;
}

// text and prose: WHERE it is, then WHAT IT READS LIKE. Segments come from the
// engine raw and are escaped here, at the one HTML boundary.
function passageFinding(stop) {
  const kindTag = stop.categories.includes('changed') ? ['ch', 'changed']
    : stop.categories.includes('added') ? ['ad', 'added']
      : ['rm', 'removed'];
  const body = stop.context
    .map((sp) => (sp.type === 'old'
      ? `<span class="old">${esc(sp.text)}</span>`
      : sp.type === 'new'
        ? `<span class="new">${esc(sp.text)}</span>`
        : `<span class="fctx">${esc(sp.text)}</span>`))
    .join('');
  const where = stop.label ? `<span class="floc">${esc(stop.label)}</span>` : '';
  return `<span class="fk ${kindTag[0]}">${kindTag[1]}</span>${where}<span>${body}</span>`;
}

// Take `cap` findings that REPRESENT every kind present, not just the first few.
// The record engine groups its changes — modifications, then additions, then
// removals — so a plain slice showed six modifications and hid every addition and
// removal from a comparison that had all three. Each present kind gets at least
// one slot; the remaining slots go to the more common kinds by largest remainder;
// and the picked findings are re-sorted into their original positions, so the list
// still reads in the engine's order rather than in category blocks.
function previewEntries(entries, cap) {
  if (entries.length <= cap) return entries;
  const buckets = new Map();
  entries.forEach((c, i) => {
    const kind = c.kind || 'change';
    if (!buckets.has(kind)) buckets.set(kind, []);
    buckets.get(kind).push(i);
  });
  // More kinds than slots: keep the most common ones. The overflow count still
  // states how many findings are not shown, so nothing is silently dropped.
  const groups = [...buckets.values()].sort((a, b) => b.length - a.length).slice(0, cap);
  const quota = groups.map(() => 1);
  const ideal = groups.map((g) => (g.length / entries.length) * cap);
  let left = cap - groups.length;
  while (left > 0) {
    let best = -1;
    for (let i = 0; i < groups.length; i++) {
      if (quota[i] >= groups[i].length) continue; // never ask a kind for more than it has
      if (best === -1 || ideal[i] - quota[i] > ideal[best] - quota[best]) best = i;
    }
    if (best === -1) break;
    quota[best]++;
    left--;
  }
  return groups
    .flatMap((g, i) => g.slice(0, quota[i]))
    .sort((a, b) => a - b)
    .map((i) => entries[i]);
}

function finding(fk, tag, body) {
  return `<span class="finding">${part(fk, tag, body)}</span>`;
}

// The inner half of a finding, so the same builders serve a plain note (a span)
// and a jumpable finding (a button).
function part(fk, tag, body) {
  return `<span class="fk ${fk}">${tag}</span><span>${body}</span>`;
}

// Show ONLY the changed span (from the inline diff) so the Crux surfaces the
// actual edit, not a truncated prefix that hides it (`…lazy dog` → `…lazy cat`).
function changedSpan(c) {
  if (c.inline) {
    const o = c.inline.filter((s) => s.type === 'old').map((s) => s.text).join('').trim();
    const n = c.inline.filter((s) => s.type === 'new').map((s) => s.text).join('').trim();
    if (o || n) return `<span class="old">${esc(trunc(o, 40))}</span> → <span class="new">${esc(trunc(n, 40))}</span>`;
  }
  return `<span class="old">${esc(trunc(c.oldText, 40))}</span> → <span class="new">${esc(trunc(c.newText, 40))}</span>`;
}

function displayPath(c) {
  const positional = typeof c.recordKey === 'string' ? c.recordKey.match(/^#(\d+)$/) : null;
  if (!positional) return c.path;
  return c.path === '$' ? `[${positional[1]}]` : `[${positional[1]}].${c.path}`;
}

function findingBody(c) {
  if (c.kind === 'column-type') return part('wn', 'schema', `column <b>${esc(c.name)}</b>: ${esc(c.oldType)} → ${esc(c.newType)}`);
  if (c.kind === 'invalid-key') return part('wn', 'key ⚠', `key <b>${esc(c.key)}</b> contains missing or non-scalar values — matched by row position instead`);
  // schema / integrity notes (record formats)
  if (c.kind === 'column-remove') return part('rm', 'column', `column <b>${esc(c.name)}</b> removed`);
  if (c.kind === 'column-add') return part('ad', 'column', `column <b>${esc(c.name)}</b> added`);
  if (c.kind === 'keywarn') return part('wn', 'key ⚠', `duplicate key <b>${esc(c.key)}</b> — matched by row position instead`);

  // record-level (CSV) findings
  if (c.kind === 'record-modify') {
    const cells = c.cells
      .map((cell) => `${esc(cell.name)} <span class="old">${esc(String(cell.oldValue ?? ''))}</span>→<span class="new">${esc(String(cell.newValue ?? ''))}</span>`)
      .join(', ');
    return part('ch', 'changed', `<b>${esc(c.recordKey)}</b>: ${cells}`);
  }

  // structural (path-based) findings
  if (c.kind === 'reorder') return part('ok', 'ignored', 'order changed — matched by key, not a change');
  if (c.kind === 'type') {
    return part('wn', 'type ⚠', `<b>${esc(displayPath(c))}</b>: ${val(c.oldValue)} → ${val(c.newValue)} <span class="warnb">${esc(c.oldType)} → ${esc(c.newType)}</span>`);
  }
  if (c.kind === 'record-add') return part('ad', 'added', `record <b>${esc(c.recordKey)}</b>`);
  if (c.kind === 'record-remove') return part('rm', 'removed', `record <b>${esc(c.recordKey)}</b>`);
  if (c.path !== undefined) {
    if (c.kind === 'change') return part('ch', 'changed', `<b>${esc(displayPath(c))}</b>: <span class="old">${val(c.oldValue)}</span> → <span class="new">${val(c.newValue)}</span>`);
    if (c.kind === 'add') return part('ad', 'added', `<b>${esc(displayPath(c))}</b>: ${val(c.newValue)}`);
    if (c.kind === 'remove') return part('rm', 'removed', `<b>${esc(displayPath(c))}</b>`);
  }

  // text / prose (line/word-based) findings
  if (c.kind === 'move') {
    return part('mv', 'moved', `moved ${c.fromStart === c.fromEnd ? 'line' : 'lines'} <b>${lineRange(c.fromStart, c.fromEnd)}</b> → <b>${lineRange(c.toStart, c.toEnd)}</b>${c.editedCount ? ` · ${c.editedCount} edited inside` : ''}`);
  }
  if (c.kind === 'change') return part('ch', 'changed', changedSpan(c));
  if (c.kind === 'add') return part('ad', 'added', `<b>${esc(trunc(c.text))}</b> added`);
  if (c.kind === 'remove') return part('rm', 'removed', `<b>${esc(trunc(c.text))}</b> removed`);
  return part('ok', 'note', '');
}
